// Heatmap time ruler — tick step that follows the zoom. Pure, no Angular.

// Candidate steps, seconds. The ruler picks the smallest one whose ticks stay at least
// MIN_TICK_SPACING_PX apart, so labels never collide at any zoom. Sub-minute steps are for
// the deep zoom used to edit clip edges (up to STEP_MAX px per 15s bin).
const RULER_STEPS_SEC = [5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 10800];
const MIN_TICK_SPACING_PX = 80;
// A label needs this much room before the end of the bars: one sticking out past the last
// bar widened the scroller's overflow — at «whole stream» zoom it scrolled by a few px.
// «1:05» vs «1:05:30» at 12px mono.
const LABEL_ROOM_PX = 40;
const LABEL_ROOM_SECONDS_PX = 64;

export function pickRulerStep(pxPerSec: number): number {
  for (const step of RULER_STEPS_SEC) {
    if (step * pxPerSec >= MIN_TICK_SPACING_PX) return step;
  }
  return RULER_STEPS_SEC[RULER_STEPS_SEC.length - 1];
}

/** «1:05» — hours:minutes; «1:05:30» when the ruler ticks finer than a minute. */
export function formatRulerLabel(t: number, step = 60): string {
  if (step < 60) {
    const s = Math.round(t);
    return `${Math.floor(s / 3600)}:${String(Math.floor(s % 3600 / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  }
  const totalMin = Math.round(t / 60);
  return `${Math.floor(totalMin / 60)}:${String(totalMin % 60).padStart(2, '0')}`;
}

export interface RulerTick {
  t: number;       // seconds — stable identity across zoom (x changes, t doesn't)
  x: number;       // px from the left edge of the bar track
  label: string;
  major: boolean;  // full hour (full minute when ticking in seconds)
}

/** Ticks in [fromSec, toSec] only — zoomed in on a long stream the whole-stream list would
 *  be thousands of ticks. No tick at 0 or at endSec. */
export function buildRulerTicks(endSec: number, pxPerSec: number, fromSec = 0, toSec = Infinity): RulerTick[] {
  if (!(endSec > 0) || !(pxPerSec > 0)) return [];
  const step = pickRulerStep(pxPerSec);
  const room = step < 60 ? LABEL_ROOM_SECONDS_PX : LABEL_ROOM_PX;
  const majorEvery = step < 60 ? 60 : 3600;
  const ticks: RulerTick[] = [];
  for (let t = Math.max(step, Math.ceil(fromSec / step) * step); t < endSec && t <= toSec; t += step) {
    const x = t * pxPerSec;
    const label = x + room > endSec * pxPerSec ? '' : formatRulerLabel(t, step);
    ticks.push({ t, x, label, major: t % majorEvery === 0 });
  }
  return ticks;
}
